import React from 'react';
import { useTranslation } from 'react-i18next';
import centralLimitsService, { UsergroupLimits } from '@/core/services/CentralLimitsService';
import { WarningBanner } from '@/core/components/WarningBanner/WarningBanner';
import './DataContractCard.scss';

const WARNING_THRESHOLD = 70;
const EXCEEDED_THRESHOLD = 100;

export interface DataContractCardProps {
  accountId?: string;
}

type UsageLevel = 'normal' | 'warning' | 'exceeded';

const getUsageLevel = (percentage: number): UsageLevel => {
  if (percentage >= EXCEEDED_THRESHOLD) return 'exceeded';
  if (percentage >= WARNING_THRESHOLD) return 'warning';
  return 'normal';
};

export const DataContractCard: React.FC<DataContractCardProps> = ({ accountId }) => {
  const { t, i18n } = useTranslation();
  const [limits, setLimits] = React.useState<UsergroupLimits | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    const loadLimits = async () => {
      try {
        const response = await centralLimitsService.getLimitByAccountId(accountId);

        if (isMounted) {
          setLimits(response?.usergroup_limits ?? null);
        }
      } catch {
        if (isMounted) {
          setLimits(null);
        }
      }
    };

    void loadLimits();

    return () => {
      isMounted = false;
    };
  }, [accountId]);

  // The card is only shown when the account has account-level (usergroup) limits.
  if (!limits) {
    return null;
  }

  const collected = limits.total_month_posts ?? 0;
  const limit = limits.month_posts_limit ?? 0;
  const percentage = limit > 0 ? (collected / limit) * 100 : 0;
  const barWidth = Math.min(percentage, 100);
  const usageLevel = getUsageLevel(percentage);
  const exceededLinkUrl = t('accountManager.exceededLinkUrl');

  return (
    <div className="data-contract-card" data-testid="data-contract-card">
      <br />
      <hr className="data-contract-card__divider" />
      <span className="data-contract-card__title" data-testid="data-contract-card-title">{t('accountManager.title')}</span>
      <p className="data-contract-card__description">
        {t('accountManager.description', { limit: limit.toLocaleString(i18n.language) })}
      </p>
      {usageLevel === 'warning' && (
        <WarningBanner
          className="data-contract-card__banner"
          message={t('accountManager.warningMessage')}
          variant="warning"
          context="outside"
          close={false}
        />
      )}
      {usageLevel === 'exceeded' && (
        <WarningBanner
          className="data-contract-card__banner"
          message={t('accountManager.exceededMessage')}
          link={exceededLinkUrl || undefined}
          linkText={t('accountManager.exceededLinkText')}
          variant="error"
          context="outside"
          close={false}
        />
      )}
      <div className="data-contract-card__values">
        <span className="data-contract-card__value">{collected.toLocaleString(i18n.language)}</span>
        <span className="data-contract-card__value">{limit.toLocaleString(i18n.language)}</span>
      </div>
      <div className="data-contract-card__progress-container">
        <div
          className={`data-contract-card__progress-fill data-contract-card__progress-fill--${usageLevel}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="data-contract-card__captions">
        <span className="data-contract-card__caption">{t('accountManager.volumeLabel')}</span>
        <span className="data-contract-card__caption">{t('accountManager.limitLabel')}</span>
      </div>
    </div>
  );
};
